import he from 'he';
import Joi from 'joi';
import { union } from 'lodash';
import { result } from 'lodash';
import { isEmpty } from 'lodash';
import { isObject } from 'lodash';
import { flatten } from 'lodash';

const JoiValidationStrategy = {
  validate: function (joiSchema, data, key) {
    if (!Joi.isSchema(joiSchema)) {
      return JoiValidationStrategy.validate(Joi.object(joiSchema), data, key);
    }

    joiSchema = joiSchema || {};
    data = data || {};
    var joiOptions = {
      abortEarly: false,
      allowUnknown: true
    };

    var errors = this._format(joiSchema.validate(data, joiOptions));
    if (key === undefined) {
      union(Object.keys(joiSchema), Object.keys(data)).forEach(function (error) {
        errors[error] = errors[error] || [];
      });
      return errors;
    } else {
      var result = errors;
      result[key] = errors[key];
      return result;
    }
  },

  getValidationMessages: function (errors, key) {
    errors = errors || {};
    if (isEmpty(errors)) {
      return [];
    } else {
      if (key === undefined) {
        return flatten(
          Object.keys(errors).map(function (error) {
            return errors[error] || [];
          })
        );
      } else {
        return errors[key] ? errors[key].map(he.decode) : [];
      }
    }
  },

  isValid: function (errors, key) {
    return isEmpty(this.getValidationMessages(errors, key));
  },
  _format: function (joiResult) {
    if (joiResult.error) {
      return joiResult.error.details.reduce(function (memo, detail) {
        const path = detail.path.join('.');
        if (!Array.isArray(memo[path])) {
          memo[path] = [];
        }
        memo[path].push(detail.message);
        return memo;
      }, {});
    } else {
      return {};
    }
  }
};

const ValidationMixin = {
  /**
   * Check for sane configurations
   */
  componentDidMount: function () {
    if (this.validatorTypes !== undefined && !isObject(this.validatorTypes)) {
      throw Error('invalid `validatorTypes` type');
    }
    if (this.getValidatorData !== undefined && !isObject(this.getValidatorData)) {
      throw Error('invalid `getValidatorData` type');
    }
  },

  /**
   * Method to validate single form key or entire form against the component data.
   *
   * @param {String|Function} key to validate, or error-first containing the validation errors if any.
   * @param {?Function} error-first callback containing the validation errors if any.
   */
  validate: function (key, callback) {
    if (typeof key === 'function') {
      callback = key;
      key = undefined;
    }
    var schema = result(this, 'validatorTypes') || {};
    var data = result(this, 'getValidatorData') || this.state;
    var validationErrors = Object.assign({}, JoiValidationStrategy.validate(schema, data, key));
    this.setState(
      {
        errors: validationErrors
      },
      this._invokeCallback.bind(this, key, callback)
    );
  },

  /**
   * Convenience method to validate a key via an event handler. Useful for
   * onBlur, onClick, onChange, etc...
   *
   * @param {?String} State key to validate
   * @return {function} validation event handler
   */
  handleValidation: function (key, callback) {
    return function (event) {
      event.preventDefault();
      this.validate(key, callback);
    }.bind(this);
  },

  /**
   * Get current validation messages for a specified key or entire form.
   *
   * @param {?String} key to get messages, or entire form if key is undefined.
   * @return {Array}
   */
  getValidationMessages: function (key) {
    return JoiValidationStrategy.getValidationMessages(this.state.errors, key);
  },

  /**
   * Clear all previous validations
   *
   * @return {void}
   */
  clearValidations: function () {
    return this.setState({
      errors: {}
    });
  },

  /**
   * Check current validity for a specified key or entire form.
   *
   * @param {?String} key to check validity (entire form if undefined).
   * @return {Boolean}.
   */
  isValid: function (key) {
    return JoiValidationStrategy.isValid(this.state.errors, key);
  },

  /**
   * Private method that handles executing users callback on validation
   *
   * @param {Object} errors object keyed on data field names.
   * @param {Function} error-first callback containing the validation errors if any.
   */
  _invokeCallback: function (key, callback) {
    if (typeof callback !== 'function') {
      return;
    }
    if (this.isValid(key)) {
      callback(null, this.state.errors);
    } else {
      callback(new Error('Validation errors exist'), this.state.errors);
    }
  }
};

export default ValidationMixin;
